import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Slider, Space, Tag, Typography } from 'antd';
import { PauseCircleOutlined, PlayCircleOutlined, SoundOutlined } from '@ant-design/icons';
import { fetchSignedMediaUrl } from '../utils/media';

const { Text } = Typography;

type JobAudioSource = {
  id: string;
  durationMs: number;
};

interface JobAudioPlayerProps {
  deviceId: string;
  sources: JobAudioSource[];
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const JobAudioPlayer: React.FC<JobAudioPlayerProps> = ({ deviceId, sources }) => {
  const [audioUrls, setAudioUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentGlobalTime, setCurrentGlobalTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const pendingSeekLocalRef = useRef<number | null>(null);
  const pendingAutoPlayRef = useRef(false);

  const durationsSec = useMemo(
    () => sources.map((source) => Math.max(0, source.durationMs / 1000)),
    [sources],
  );
  const totalDuration = useMemo(
    () => durationsSec.reduce((sum, duration) => sum + duration, 0),
    [durationsSec],
  );
  const trackOffsets = useMemo(() => {
    const offsets: number[] = [];
    let offset = 0;
    durationsSec.forEach((duration) => {
      offsets.push(offset);
      offset += duration;
    });
    return offsets;
  }, [durationsSec]);
  const activeSource = sources[activeIndex];
  const activeSourceUrl = activeSource ? audioUrls[activeSource.id] : undefined;

  useEffect(() => {
    let cancelled = false;
    const loadUrls = async () => {
      if (sources.length === 0) {
        setAudioUrls({});
        return;
      }
      setLoading(true);
      try {
        const { fetchAuthSession } = await import('../auth/gateway');
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString();
        if (!token) {
          return;
        }

        const entries = await Promise.all(
          sources.map(async (source) => {
            const signedUrl = await fetchSignedMediaUrl(
              `/api/v1/devices/${deviceId}/audio/${source.id}/file`,
              token,
            );
            return [source.id, signedUrl] as const;
          }),
        );

        if (!cancelled) {
          setAudioUrls(Object.fromEntries(entries));
        }
      } catch (error) {
        console.error('Failed to load grouped audio urls', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    setActiveIndex(0);
    setCurrentGlobalTime(0);
    setIsPlaying(false);
    loadUrls();
    return () => {
      cancelled = true;
    };
  }, [deviceId, sources]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    const handleLoadedMetadata = () => {
      const trackDuration = durationsSec[activeIndex] ?? 0;
      const localTarget = clamp(pendingSeekLocalRef.current ?? 0, 0, trackDuration);
      audio.currentTime = localTarget;
      setCurrentGlobalTime((trackOffsets[activeIndex] ?? 0) + localTarget);
      if (pendingAutoPlayRef.current) {
        audio.play()
          .then(() => setIsPlaying(true))
          .catch((error) => console.error('Grouped audio playback error', error))
          .finally(() => {
            pendingAutoPlayRef.current = false;
          });
      }
      pendingSeekLocalRef.current = null;
    };

    const handleTimeUpdate = () => {
      setCurrentGlobalTime((trackOffsets[activeIndex] ?? 0) + audio.currentTime);
    };

    const handleEnded = () => {
      if (activeIndex < sources.length - 1) {
        pendingSeekLocalRef.current = 0;
        pendingAutoPlayRef.current = true;
        setActiveIndex((prev) => prev + 1);
        return;
      }
      setIsPlaying(false);
      setCurrentGlobalTime(totalDuration);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [activeIndex, durationsSec, sources.length, totalDuration, trackOffsets]);

  const setPlaybackPosition = useCallback((globalTimeSec: number, playAfterSeek = false) => {
    if (sources.length === 0) {
      return;
    }
    const safeGlobal = clamp(globalTimeSec, 0, totalDuration);
    let targetIndex = sources.length - 1;
    for (let index = 0; index < sources.length; index += 1) {
      const trackStart = trackOffsets[index] ?? 0;
      const trackEnd = trackStart + (durationsSec[index] ?? 0);
      if (safeGlobal <= trackEnd || index === sources.length - 1) {
        targetIndex = index;
        break;
      }
    }

    const localTarget = safeGlobal - (trackOffsets[targetIndex] ?? 0);
    const audio = audioRef.current;
    pendingSeekLocalRef.current = localTarget;
    pendingAutoPlayRef.current = playAfterSeek;

    if (targetIndex === activeIndex && audio) {
      audio.currentTime = localTarget;
      setCurrentGlobalTime(safeGlobal);
      if (playAfterSeek) {
        audio.play()
          .then(() => setIsPlaying(true))
          .catch((error) => console.error('Grouped audio seek playback error', error))
          .finally(() => {
            pendingAutoPlayRef.current = false;
          });
      } else {
        setIsPlaying(false);
      }
      pendingSeekLocalRef.current = null;
      return;
    }

    setIsPlaying(false);
    setCurrentGlobalTime(safeGlobal);
    setActiveIndex(targetIndex);
  }, [activeIndex, durationsSec, sources.length, totalDuration, trackOffsets]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !activeSourceUrl) {
      return;
    }
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }
    if (currentGlobalTime >= totalDuration && totalDuration > 0) {
      setPlaybackPosition(0, true);
      return;
    }
    try {
      await audio.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('Grouped audio playback error', error);
    }
  }, [activeSourceUrl, currentGlobalTime, isPlaying, setPlaybackPosition, totalDuration]);

  const formatTime = (time: number) => {
    if (!Number.isFinite(time)) {
      return '--:--';
    }
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ width: '100%' }}>
      <audio ref={audioRef} src={activeSourceUrl} preload="metadata" />
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button
            type="primary"
            shape="circle"
            icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
            onClick={togglePlay}
            size="large"
            loading={loading}
            disabled={!activeSourceUrl || totalDuration <= 0}
          />
          <SoundOutlined />
          <Text type="secondary">
            {formatTime(currentGlobalTime)} / {formatTime(totalDuration)}
          </Text>
        </div>
        <Slider
          min={0}
          max={Math.max(totalDuration, 0.001)}
          value={Math.min(currentGlobalTime, totalDuration)}
          onChange={(value) => setPlaybackPosition(Array.isArray(value) ? value[0] : value)}
          tooltip={{ formatter: (value) => formatTime(value ?? 0) }}
        />
        <Space size={6} wrap>
          {sources.map((source, index) => (
            <Tag key={source.id} color={index === activeIndex ? 'blue' : 'default'}>
              #{index + 1} {formatTime(durationsSec[index] ?? 0)}
            </Tag>
          ))}
        </Space>
      </Space>
    </div>
  );
};

export default JobAudioPlayer;
